import { RAGNA } from '@somnhold/content';
import { iatan2, idiv, imax, imin, mulBp, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, UnsleepingState } from '../../types.ts';
import { damageHero, damageStructure, ev } from '../combat.ts';
import { chaseStep } from '../enemies.ts';
import { at, by, heroesNear, held, holdable, markBlast, nearestHero, reachable, said, standing, type Kit } from './kit.ts';

/*
 * Ragna, the Horn (barbarian): she went out through the gate to hunt them, alone. Her fury grows as she bleeds and
 * never ebbs, nothing slows her and she mends on every blow; she leaps onto whoever shoots from farthest (twice from
 * her second phase), her horn breaks the heroes' nerve and lifts the nightmares, and the lone sally — her ultimate —
 * is a whirlwind toward the heroes that nothing stops. In her last phase, frenzy: she strikes much sooner.
 */

// cds: leap, horn. k: what her multi-tick action is (LEAPING or SALLY), whether this leap is the second of a pair.
// busy/vx/vy: the leap's flight or the whirlwind's ticks; tgts: the hero she leaps on.
const LEAP = 0;
const HORN = 1;
const MODE = 0;
const SECOND = 1;
const LEAPING = 1;
const SALLY = 2;

/** Fury that doesn't ebb: how much harder she hits for the life she lost (bp). */
const fury = (e: Enemy): number => 10000 + RAGNA.furyPerTenthBp * idiv((e.maxHp - imax(0, e.hp)) * 10, imax(1, e.maxHp));

/** Her power on a hero (`dmg` already scaled by her strength): with her fury, at least a share of its health; she mends on what it took. */
const bite = (s: BattleState, e: Enemy, h: Hero, dmg: number, maxHpBp: number): void => {
  const f = fury(e);
  const before = h.hp;
  damageHero(s, h, by(h, mulBp(dmg, f), mulBp(maxHpBp, f)), e, undefined, true);
  const dealt = before - imax(0, h.hp);
  if (dealt <= 0) return;
  const l = RAGNA.lifesteal;
  e.hp = imin(e.maxHp, e.hp + imax(mulBp(dealt, l.dealtBp), mulBp(e.maxHp, l.minMaxHpBp)));
};

/** Whom she leaps on: the one who shoots from farthest; with none, the most wounded (never the one she just left). */
const prey = (s: BattleState, e: Enemy, skip: number): Hero | null => {
  const l = RAGNA.leap;
  let best: Hero | null = null;
  let bk = 0;
  for (const h of s.heroes) {
    if (!standing(h) || h.id === skip || !reachable(s, e, h)) continue;
    const dx = h.x - e.x;
    const dy = h.y - e.y;
    const d = dx * dx + dy * dy;
    if (d > l.range * l.range || d < l.minRange * l.minRange) continue;
    // Ranged ones first, the farthest of them; else the lowest share of health left.
    const k = h.stats.range >= l.rangedFrom ? 0x7fffffff - idiv(d, 1024) : 0x10000000 - idiv(imax(0, h.hp) * 10000, imax(1, h.stats.maxHp));
    if (!best || k > bk) {
      best = h;
      bk = k;
    }
  }
  return best;
};

/** Takes off toward her prey: a marked ring where she lands. */
const leap = (s: BattleState, e: Enemy, u: UnsleepingState, skip = 0): boolean => {
  const l = RAGNA.leap;
  const h = prey(s, e, skip);
  if (!h) return false;
  u.busy = l.ticks;
  u.k[MODE] = LEAPING;
  u.vx = idiv(h.x - e.x, l.ticks);
  u.vy = idiv(h.y - e.y, l.ticks);
  u.tgts = [h.id];
  e.facing = iatan2(h.y - e.y, h.x - e.x);
  e.anim = 'attack';
  e.animT = 0;
  const f = fury(e);
  markBlast(s, u, e.x + u.vx * l.ticks, e.y + u.vy * l.ticks, { warn: l.ticks + 1, radius: l.radius, damage: mulBp(l.damage, f), hpBp: mulBp(l.maxHpBp, f), stun: l.stun });
  said(s, e, u, 'Leap', h.x, h.y);
  return true;
};

/** Her horn: the heroes near lose their nerve (her control: once in its immunity), the nightmares near are lifted. */
const horn = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const c = RAGNA.horn;
  const near = heroesNear(s, e.x, e.y, c.radius);
  if (!near.length) {
    u.cds[HORN] = 6;
    return false;
  }
  for (const h of near) {
    if (!holdable(s, h)) continue;
    h.fear = imax(h.fear ?? 0, c.fear);
    held(s, h);
  }
  for (const o of s.enemies) if (o.hp > 0 && withinRange(e.x, e.y, o.x, o.y, c.rallyRadius)) o.rallied = s.tick + c.rally;
  u.cds[HORN] = c.every;
  e.anim = 'cast';
  e.animT = 0;
  said(s, e, u, 'Horn');
  ev(s, { t: 'boom', x: e.x, y: e.y, r: c.radius, kind: 'ragnaHorn' });
  return true;
};

/** A tick of her leap in flight; landed, she stays on her prey (and from her second phase leaps once more). */
const flight = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  e.x += u.vx;
  e.y += u.vy;
  e.anim = 'walk';
  if (--u.busy > 0) return true;
  u.k[MODE] = 0;
  e.next = -1;
  const h = s.heroes.find((o) => o.id === u.tgts[0]);
  if (h && standing(h)) {
    e.taunter = h.id;
    e.tauntTicks = RAGNA.leap.hunt;
  }
  if (e.phase >= 1 && u.k[SECOND] === 0) {
    u.k[SECOND] = 1;
    if (leap(s, e, u, h?.id ?? 0)) return true;
  }
  u.k[SECOND] = 0;
  return true;
};

/** A tick of the whirlwind: toward the nearest hero, nothing holds her, and every so often it strikes all around. */
const whirl = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const w = RAGNA.sally;
  u.unstoppable = imax(u.unstoppable, 2);
  e.stun = 0;
  const h = nearestHero(s, e, w.range, (o) => reachable(s, e, o));
  if (h && !withinRange(e.x, e.y, h.x, h.y, 300)) chaseStep(s, e, h.x, h.y, w.step);
  e.anim = 'walk';
  if (u.busy % w.every === 0) {
    for (const o of heroesNear(s, e.x, e.y, w.radius)) bite(s, e, o, mulBp(w.damage, u.dmgBp), w.maxHpBp);
    for (const st of s.structures) {
      if (st.destroyed || st.maxHp <= 0) continue;
      const c = at(st);
      if (withinRange(e.x, e.y, c.x, c.y, w.radius)) damageStructure(s, st, mulBp(w.structure, u.dmgBp), e);
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: w.radius, kind: 'ragnaWhirl' });
  }
  if (--u.busy === 0) {
    u.k[MODE] = 0;
    e.next = -1;
  }
  return true;
};

export const ragna: Kit = {
  init: (_s, _e, u) => {
    u.cds = [idiv(RAGNA.leap.every, 2), idiv(RAGNA.horn.every, 2)];
    u.k = [0, 0];
  },
  busy: (s, e, u) => (u.k[MODE] === SALLY ? whirl(s, e, u) : flight(s, e, u)),
  act: (s, e, u) => {
    if (u.cds[HORN] === 0 && horn(s, e, u)) return true;
    if (u.cds[LEAP] !== 0) return false;
    if (!leap(s, e, u)) {
      u.cds[LEAP] = 6;
      return false;
    }
    u.cds[LEAP] = RAGNA.leap.every;
    return true;
  },
  // The sally starts where she stands, toward the nearest hero in her reach (none: not yet).
  aim: (s, e, u) => {
    const w = RAGNA.sally;
    const h = nearestHero(s, e, w.range, (o) => reachable(s, e, o));
    if (!h) return false;
    u.castA = iatan2(h.y - e.y, h.x - e.x);
    e.facing = u.castA;
    u.spots = [{ x: e.x, y: e.y, r: w.radius }];
    said(s, e, u, 'Sally');
    return true;
  },
  land: (s, e, u) => {
    u.busy = RAGNA.sally.ticks;
    u.k[MODE] = SALLY;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: RAGNA.sally.radius, kind: 'ragnaCry' });
  },
  strike: (s, e, h, dmg) => bite(s, e, h, dmg, RAGNA.blowMaxHpBp),
  // Frenzy: from her last phase she strikes much sooner.
  phase: (_s, e) => {
    if (e.phase === 2) {
      e.reload = mulBp(e.reload, RAGNA.frenzyReloadBp);
      e.cd = imin(e.cd, e.reload);
    }
  },
  // Nothing slows her.
  status: (_s, e) => {
    e.slowBp = 0;
    e.slowTicks = 0;
  },
};
